/**
 * Ejar's E-Checks, bookings and vehicles from the engine's PostgreSQL store (owner, 27 Sep 2026: "we will not rely on
 * Firebase anymore"), in the row shapes the pages read today - so a page changes its source, not its logic. Used
 * only when REACT_APP_CONTRACTS_V2=true (the local test site); the live site keeps its Firestore reads.
 *
 *   E-Checks  GET /api/store/checks   -> the E-Check archive row (archive_chunks, the engine's ejar_sync columns)
 *   Bookings  GET /api/store/bookings -> the `ejar_bookings` document (firestore_apply.BOOKING_FIELD_MAP)
 *   Vehicles  GET /api/store/vehicles -> Ejar's side of a fleet car (status, branch, rate, KM), laid over the
 *             `fleet` document by plate with the engine's own rule (firestore_apply.merge_fleet)
 *
 * Days go to the engine as "yyyy-mm-dd" one day wider on each side, and the rows are kept to the exact window
 * here: the same answer whether the engine reads a day as its start or its end, in UTC or in Dubai.
 */
import { finesService } from './finesService';
import { dubaiText } from './contractsV2';
import logger from '../utils/logger';

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());
const DAY_MS = 24 * 60 * 60 * 1000;
const DUBAI_MS = 4 * 60 * 60 * 1000;

/** `fn` over `items`, at most `size` at a time; the results in the items' order. A failure rejects the whole. */
export async function mapPool(items, size, fn) {
    const list = [...(items || [])];
    const out = new Array(list.length);
    let next = 0;
    const worker = async () => {
        while (next < list.length) {
            const i = next;
            next += 1;
            out[i] = await fn(list[i], i);
        }
    };
    await Promise.all(Array.from({ length: Math.min(Math.max(1, size), list.length) }, worker));
    return out;
}

/** "yyyy-mm-dd" of an instant, on Dubai's calendar. */
export const dubaiDay = (ms) => new Date(ms + DUBAI_MS).toISOString().slice(0, 10);

/** An ISO time (any zone) or "dd/mm/yyyy HH:MM" as the Dubai wall text "dd/mm/yyyy HH:MM" the old documents kept. */
export const wallText = (v) => {
    const t = text(v);
    if (!t) return '';
    if (/^\d{2}\/\d{2}\/\d{4}/.test(t)) return t;
    return dubaiText(t);
};

/** The instant of an ISO time in ms (digits past the milliseconds cut: not every browser reads 6), or null. */
export const isoTime = (v) => {
    const t = text(v);
    if (!t) return null;
    const ms = new Date(t.replace(/(\.\d{3})\d+/, '$1')).getTime();
    return Number.isNaN(ms) ? null : ms;
};

// ------------------------------------------------------------------------------------------------ E-Checks
/** Ejar's "has damages" as the archive wrote it ("Exist" / "Not Exist"). */
const damagesText = (v) => {
    if (typeof v === 'string') {
        const t = v.trim();
        if (/^not\s*exist$/i.test(t) || /^(no|false|0)?$/i.test(t)) return 'Not Exist';
        return /exist|yes|true|1/i.test(t) ? 'Exist' : 'Not Exist';
    }
    return v ? 'Exist' : 'Not Exist';
};

/**
 * One check of /api/store/checks as the E-Check archive row every page reads (engine ejar_sync._echeck_rows):
 * E-Check Number, Plate Number, Odometer, Fuel, Model, Branch, Transaction Date (Dubai "dd/mm/yyyy HH:MM"),
 * Check Type, Contract No., Has Damages ("Exist" / "Not Exist"). `Fuel Level %` and `check_id` ride along.
 */
export const echeckRowOf = (item) => {
    const odometer = item?.odometer;
    return {
        'E-Check Number': text(item?.check_number),
        'Plate Number': text(item?.plate),
        Odometer: odometer === null || odometer === undefined ? '' : odometer,
        Fuel: text(item?.fuel),
        'Fuel Level %': item?.fuel_pct === null || item?.fuel_pct === undefined ? '' : item.fuel_pct,
        Model: text(item?.model),
        Branch: text(item?.branch),
        'Transaction Date': wallText(item?.transaction_at),
        'Check Type': text(item?.check_type),
        'Contract No.': text(item?.contract_no),
        'Has Damages': damagesText(item?.has_damages),
        check_id: item?.check_id ?? null,
        _at: isoTime(item?.transaction_at),
        // the contract's open / close at Ejar's earlier time (the engine's rule, owner 6 Oct 2026): the check's own
        // time kept beside it - History Lens shows it when they are more than an hour apart
        ...(item?.time_source === 'ejar' ? {
            'E-Check Time': wallText(item?.echeck_at),
            Source: 'Ejar',
            _gapMs: Math.abs((isoTime(item?.echeck_at) ?? 0) - (isoTime(item?.transaction_at) ?? 0)),
        } : {}),
    };
};

const CHECKS_KEEP_MS = 30 * 1000;      // one answer serves the same question for 30 s (a page's parallel asks)
const checkReads = new Map();          // query -> { at, promise }

/** One /api/store/checks answer as archive rows (kept 30 s by its query; a failed read is not kept). */
const readChecks = (params) => {
    const key = JSON.stringify(params);
    const now = Date.now();
    const kept = checkReads.get(key);
    if (kept && now - kept.at < CHECKS_KEEP_MS) return kept.promise;
    const promise = finesService.storeChecks(params).then((answer) => (answer?.items || []).map(echeckRowOf));
    checkReads.set(key, { at: now, promise });
    promise.catch(() => { if (checkReads.get(key)?.promise === promise) checkReads.delete(key); });
    return promise;
};

/** Forget the answers kept for 30 s. */
export const forgetCheckReads = () => checkReads.clear();

/** Rows without repeats (the same check read twice: by its id, else its number, else contract + plate + time). */
export const uniqueChecks = (rows) => {
    const seen = new Set();
    return (rows || []).filter((r) => {
        const key = r.check_id != null ? `#${r.check_id}` : (r['E-Check Number'] ? `n${r['E-Check Number']}`
            : `${r['Contract No.']}|${r['Plate Number']}|${r['Transaction Date']}|${r['Check Type']}`);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const byTime = (a, b) => (a._at ?? 0) - (b._at ?? 0);
const CHECKS_PER_KEY = 5000;           // every check of one contract / one car (a car: a few hundred in 5 years)
const POOL = 6;                        // requests at a time

/** Every check of these contracts (the archive's check types), oldest first. */
export async function checksForContracts(contractNos, { kind = 'contract' } = {}) {
    const ids = [...new Set((contractNos || []).map((c) => text(c).toUpperCase()).filter(Boolean))];
    const lists = await mapPool(ids, POOL, (contract) => readChecks({ contract, kind, limit: CHECKS_PER_KEY }));
    return uniqueChecks(lists.flat()).sort(byTime);
}

/** Every check of these cars (plates as written), oldest first. */
export async function checksForPlates(plates, { kind = 'contract' } = {}) {
    const list = [...new Set((plates || []).map(text).filter(Boolean))];
    const lists = await mapPool(list, POOL, (plate) => readChecks({ plate, kind, limit: CHECKS_PER_KEY }));
    return uniqueChecks(lists.flat()).sort(byTime);
}

const WINDOW_SLICE_DAYS = 7;
const WINDOW_LIMIT = 5000;

/**
 * Every check between two instants (ms), oldest first: a week a request (days sent one wider each side and the rows
 * kept to the window); a slice that comes back full (the engine's `limit`) is read again in halves, down to a day -
 * never a window cut short without a word. `onProgress(done, total)` per slice.
 */
export async function checksInWindow(fromMs, toMs, { kind = 'contract', onProgress } = {}) {
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) return [];
    const slices = [];
    for (let s = fromMs; s <= toMs; s += WINDOW_SLICE_DAYS * DAY_MS) slices.push([s, Math.min(toMs, s + WINDOW_SLICE_DAYS * DAY_MS - 1)]);
    let done = 0;
    const readSlice = async ([a, b]) => {
        const rows = await readChecks({ from: dubaiDay(a - DAY_MS), to: dubaiDay(b + DAY_MS), kind, limit: WINDOW_LIMIT });
        if (rows.length >= WINDOW_LIMIT && b - a > DAY_MS) {
            const mid = a + Math.floor((b - a) / 2);
            return [...await readSlice([a, mid]), ...await readSlice([mid + 1, b])];
        }
        if (rows.length >= WINDOW_LIMIT) logger.warn(`[checks] ${dubaiDay(a)}: ${rows.length} checks, the engine's limit - some may be missing`);
        return rows.filter((r) => r._at !== null && r._at >= a && r._at <= b);
    };
    const lists = await mapPool(slices, 3, async (slice) => {
        const rows = await readSlice(slice);
        done += 1;
        if (onProgress) onProgress(done, slices.length);
        return rows;
    });
    return uniqueChecks(lists.flat()).sort(byTime);
}

/** The latest check of each car (plates as written) -> Map(plate as given -> archive row). */
export async function latestCheckByPlate(plates, { kind = 'contract' } = {}) {
    const list = [...new Set((plates || []).map(text).filter(Boolean))];
    const lists = await mapPool(list, POOL, (plate) => readChecks({ plate, kind, limit: CHECKS_PER_KEY }));
    const out = new Map();
    list.forEach((plate, i) => {
        const latest = (lists[i] || []).reduce((best, r) => (!best || (r._at ?? 0) >= (best._at ?? 0) ? r : best), null);
        if (latest) out.set(plate, latest);
    });
    return out;
}

// ------------------------------------------------------------------------------------------------ Bookings
const first = (item, ...keys) => {
    for (const k of keys) {
        const v = item?.[k];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
};

/** "dd/mm/yyyy ..." -> "yyyy-mm-dd" (the page groups bookings by pick-up day). */
const dayOfWall = (wall) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(text(wall));
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

/**
 * One booking of /api/store/bookings as the `ejar_bookings` document (engine firestore_apply.merge_booking_rows):
 * the Firestore field names when the engine sends them, else the store's own columns (pickup_at, customer_name,
 * phone, plate, broker_ref, duration_type, ejar_created_at ...) turned into them - times as Dubai "dd/mm/yyyy HH:MM".
 */
export const bookingOf = (item) => {
    const bookingNo = text(first(item, 'booking_number')).toUpperCase();
    const pickup = wallText(first(item, 'pickup_date', 'pickup_at'));
    const statusId = first(item, 'status_id');
    return {
        id: bookingNo,
        booking_number: bookingNo,
        broker_reference: text(first(item, 'broker_reference', 'broker_ref')),
        vehicle: text(first(item, 'vehicle')),
        plate_no: text(first(item, 'plate_no', 'plate')),
        customer: text(first(item, 'customer', 'customer_name')),
        mobile: text(first(item, 'mobile', 'phone', 'phone_raw')),
        pickup_date: pickup,
        dropoff_date: wallText(first(item, 'dropoff_date', 'dropoff_at')),
        pickup_day: text(first(item, 'pickup_day')) || dayOfWall(pickup),
        pickup_branch: text(first(item, 'pickup_branch')),
        dropoff_branch: text(first(item, 'dropoff_branch')),
        status: text(first(item, 'status')),
        status_id: statusId === '' ? '' : Number(statusId),
        booking_type: text(first(item, 'booking_type')),
        duration: text(first(item, 'duration', 'duration_type')),
        source: text(first(item, 'source')),
        payment_term: text(first(item, 'payment_term')),
        balance: first(item, 'balance'),
        created_by: text(first(item, 'created_by')),
        created_at: wallText(first(item, 'created_at', 'ejar_created_at')),
        last_update: wallText(first(item, 'last_update', 'ejar_modified_at')),
        last_seen: text(first(item, 'last_seen', 'updated_at')),
    };
};

const BOOKINGS_KEEP_MS = 30 * 1000;    // the page and its "updated at" ask for the same days at once: one read
const bookingReads = new Map();        // query -> { at, promise }

/** Forget the bookings kept for 30 s. */
export const forgetBookingReads = () => bookingReads.clear();

/** Bookings whose Dubai pick-up day is between two "yyyy-mm-dd" days (both included), as `ejar_bookings` docs. */
export async function bookingsForDays(fromIso, toIso, { status } = {}) {
    const from = dubaiDay(Date.parse(`${fromIso}T00:00:00Z`) - DAY_MS);
    const to = dubaiDay(Date.parse(`${toIso}T00:00:00Z`) + DAY_MS);
    const key = `${from}|${to}|${status || ''}`;
    const now = Date.now();
    let kept = bookingReads.get(key);
    if (!kept || now - kept.at >= BOOKINGS_KEEP_MS) {
        const promise = finesService.storeBookings({ from, to, status }).then((answer) => (answer?.items || []).map(bookingOf));
        kept = { at: now, promise };
        bookingReads.set(key, kept);
        const entry = kept;
        promise.catch(() => { if (bookingReads.get(key) === entry) bookingReads.delete(key); });
    }
    return (await kept.promise).filter((b) => b.booking_number && b.pickup_day >= fromIso && b.pickup_day <= toIso);
}

/** The days the bookings page reads (yesterday to 60 days ahead, Dubai): its "updated at" comes from the same read. */
export const bookingsPageDays = (now = Date.now()) => [dubaiDay(now - DAY_MS), dubaiDay(now + 60 * DAY_MS)];

/** { synced_at, rows } as `ejar_bookings/_metadata` gave it: the newest read of the bookings (null when unknown). */
export const bookingsMetaOf = (bookings) => {
    const times = (bookings || []).map((b) => isoTime(b.last_seen)).filter((t) => t !== null);
    return { synced_at: times.length ? new Date(Math.max(...times)).toISOString() : null, rows: (bookings || []).length };
};

// ------------------------------------------------------------------------------------------------ Vehicles
/** Letters and digits, upper case ("CC 31047" -> "CC31047"): how a plate is compared. */
export const plateKeyOf = (plate) => text(plate).replace(/[^A-Za-z0-9]/g, '').toUpperCase();

/** One vehicle of /api/store/vehicles: Ejar's side of the car, in the fleet's field names. */
export const ejarVehicleOf = (item) => {
    const km = first(item, 'current_km', 'odometer');
    const rate = first(item, 'rental_rate', 'daily_rate');
    return {
        key: text(item?.plate_key) ? plateKeyOf(item.plate_key) : plateKeyOf(item?.plate),
        plate: text(first(item, 'plate', 'plate_number')),
        status: text(first(item, 'status')),
        branch: text(first(item, 'branch')),
        current_km: km === '' ? '' : text(km),
        rental_rate: rate === '' ? null : rate,
        make: text(first(item, 'make', 'manufacturer')),
        year: text(first(item, 'year')),
        on_ejar: item?.on_ejar !== false,
    };
};

export const NOT_IN_EJAR = 'Not in Ejar';

/**
 * The fleet cars with Ejar's side laid over them - the engine's hourly rule (firestore_apply.merge_fleet), applied
 * at read time: a car in the fleet (not exactly "sold", not a renamed-plate pointer) takes Ejar's status, branch,
 * rental rate and KM when Ejar has a value; a car Ejar does not list is "Not in Ejar". A car Ejar has and the fleet
 * does not is never added (it is flagged for review on the engine). The fleet's own fields (insurance, RTA,
 * remarks ...) are left as they are, except a make / model year the car has none of, which Ejar's gives - the RTA
 * master list that wrote them is gone (owner, 28 Sep 2026), and a new car of the RTA licence tool has neither
 * (the engine's fleet_identity.make_year_fill, the same rule). `vehicles`: ejarVehicleOf rows of the cars ON Ejar;
 * an empty list changes nothing (no answer is not "no car on Ejar").
 */
export function mergeFleetWithEjar(cars, vehicles) {
    const list = Array.isArray(cars) ? cars : [];
    const onEjar = (vehicles || []).filter((v) => v && v.key && v.on_ejar !== false);
    if (!onEjar.length) return list;
    const byKey = new Map(onEjar.map((v) => [v.key, v]));
    return list.map((car) => {
        if (!car || car.moved_to || car.status === 'sold') return car;
        const keys = [car.plate_number, ...(Array.isArray(car.previous_plates) ? car.previous_plates : [])].map(plateKeyOf);
        const v = keys.map((k) => byKey.get(k)).find(Boolean);
        if (!v) return car.status === NOT_IN_EJAR ? car : { ...car, status: NOT_IN_EJAR };
        const next = { ...car };
        if (v.status) next.status = v.status;
        if (v.branch) next.branch = v.branch;
        if (v.rental_rate !== null && v.rental_rate !== undefined) next.rental_rate = v.rental_rate;
        if (v.current_km) next.current_km = v.current_km;
        if (v.make && !text(car.make)) next.make = v.make;
        if (v.year && !text(car.year)) next.year = v.year;
        return next;
    });
}

const VEHICLES_KEEP_MS = 60 * 1000;
let vehiclesRead = null;               // { at, promise }

/** The cars on Ejar now (ejarVehicleOf rows), one read a minute shared by every caller. */
export function ejarVehiclesOnEjar({ force = false } = {}) {
    const now = Date.now();
    if (!force && vehiclesRead && now - vehiclesRead.at < VEHICLES_KEEP_MS) return vehiclesRead.promise;
    const promise = finesService.storeVehicles({ onEjar: true }).then((answer) => (answer?.items || []).map(ejarVehicleOf));
    const entry = { at: now, promise };
    vehiclesRead = entry;
    promise.catch(() => { if (vehiclesRead === entry) vehiclesRead = null; });
    return promise;
}

/**
 * The fleet cars with Ejar's side from the store. When the vehicles cannot be read the cars come back as the fleet
 * has them (the engine still writes Ejar's status there every hour) and the reason is logged - a failed read never
 * turns every car "Not in Ejar".
 */
export async function withEjarVehicles(cars, { force = false } = {}) {
    try {
        return mergeFleetWithEjar(cars, await ejarVehiclesOnEjar({ force }));
    } catch (e) {
        logger.warn('[fleet] Ejar vehicles not read from the store - the fleet shows its own status / branch / KM', e);
        return cars;
    }
}
